
import React, { useState } from "react";
import { apiUrl } from "../../config";
import { FullDetails } from "../../design-system/components/FullDetails";

/**
 * FACEIT Data API: Championships
 * This page calls your backend wrapper for GET /championships?game=...&type=...
 * What you get without fetching: a paged list of championships for a game (e.g., cs2),
 * filterable by type: all, upcoming, ongoing, or past. Each item includes IDs, names, regions,
 * imagery and timing; some related fields can be expanded with query params (per API rules).
 */
export function Championships() {
  const [game, setGame] = useState("cs2");
  const [type, setType] = useState<"all" | "upcoming" | "ongoing" | "past">("upcoming");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);

  async function fetchChamps() {
    setErr(null);
    setData(null);
    setLoading(true);
    try {
      const params = new URLSearchParams({ game, type });
      const res = await fetch(apiUrl(`/api/faceit/championships?${params.toString()}`));
      const json = await res.json();
      if (!res.ok || json?.error) setErr(json?.error || `HTTP ${res.status}`);
      else setData(json);
    } catch (e: any) {
      setErr(e?.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">FACEIT Championships</h2>
        <p className="text-sm text-muted">
          Returns a paged list of championships for a game (e.g., <code>cs2</code>), filtered by <code>type</code>.
        </p>
      </div>

      {/* Form */}
      <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <input
            value={game}
            onChange={(e) => setGame(e.target.value)}
            placeholder="game (e.g. cs2)"
            className="bg-bg border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#4FC3F7]"
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value as any)}
            className="bg-bg border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#4FC3F7]"
          >
            <option value="all">all</option>
            <option value="upcoming">upcoming</option>
            <option value="ongoing">ongoing</option>
            <option value="past">past</option>
          </select>
          <button
            type="button"
            onClick={fetchChamps}
            disabled={loading}
            className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-sm"
          >
            {loading ? "Loading…" : "Fetch"}
          </button>
        </div>

        {err && <pre className="text-xs text-red-400 overflow-x-auto">{err}</pre>}
      </div>

      {/* Result */}
      {data && (
        <FullDetails data={data} title="All fields" open />
      )}
    </div>
  );
}
